import { useState } from 'react';
import { SectionLabel } from '../components/Effects';
import { ArrowIcon, CopyIcon, DiscordIcon } from '../components/Icons';
import { WhitelistFlow } from '../components/WhitelistFlow';
import type { SessionUser } from '../types';

interface ApplyPageProps {
  user: SessionUser | null;
  loading: boolean;
  onUserChange: (user: SessionUser) => void;
}

export function ApplyPage({ user, loading, onUserChange }: ApplyPageProps) {
  const [copied, setCopied] = useState('');
  const copy = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(''), 1800);
  };

  const inviteUrl = import.meta.env.VITE_DISCORD_INVITE_URL || 'https://discord.gg/hAqvebcSh3';
  const loginUrl = `/auth/discord?returnTo=${encodeURIComponent('https://apply.ugeappagesmp.online/')}`;

  return (
    <div className="apply-page">
      {/* Cinematic Hero & Application Console */}
      <section className="apply-hero">
        <div className="apply-hero-bg" />
        <div className="apply-hero-vignette" />

        <div className="apply-hero-content">
          <div className="eyebrow" data-reveal>
            <span /> OFFICIAL CITIZENSHIP REGISTRY · SEASON III: THE SOVEREIGN CIVILIZATION
          </div>

          <h1 className="apply-hero-title" data-reveal>
            Apply for Whitelist<br />
            <em>&amp; Claim Your Citizenship.</em>
          </h1>

          <p className="apply-hero-desc" data-reveal>
            Step into Sri Lanka&apos;s premiere sovereign Minecraft civilization. Every applicant undergoes identity verification and a brief voice interview to maintain our high-trust economy and grief-free community.
          </p>

          <div className="apply-perks-ribbon" data-reveal>
            <div className="perk-pill">
              <span className="perk-dot" />
              <b>Anti-Grief &amp; Theft Laws</b>
            </div>
            <div className="perk-pill">
              <span className="perk-dot" />
              <b>100% Player-Driven Economy</b>
            </div>
            <div className="perk-pill">
              <span className="perk-dot" />
              <b>2-Min Voice Verification</b>
            </div>
            <div className="perk-pill">
              <span className="perk-dot" />
              <b>Java &amp; Bedrock Crossplay</b>
            </div>
          </div>

          {/* Interactive Citizenship Command Console */}
          <div className="apply-console" data-reveal>
            {loading ? (
              <div className="apply-loading-box">
                <div className="skeleton-btn" style={{ width: '220px', height: '48px', margin: '0 auto 16px' }} />
                <p>Authenticating citizen session...</p>
              </div>
            ) : !user ? (
              /* CASE 1: Not logged in with Discord */
              <div className="apply-auth-gate">
                <div className="gate-icon-badge">
                  <DiscordIcon size={40} />
                </div>
                <div className="gate-header">
                  <SectionLabel index="PHASE 01">IDENTITY VERIFICATION</SectionLabel>
                  <h2>Connect Your Discord Account</h2>
                  <p>
                    To protect citizen settlements, enforce our civic constitution, and streamline your interview, all applicants must authenticate with their official Discord profile.
                  </p>
                </div>

                <div className="gate-requirements">
                  <div className="req-card">
                    <span className="req-num">01</span>
                    <b>Personal Discord Profile</b>
                    <small>Must be an active, verified account in good standing.</small>
                  </div>
                  <div className="req-card">
                    <span className="req-num">02</span>
                    <b>SMP Community Membership</b>
                    <small>Must join the official Uge Appage SMP community server.</small>
                  </div>
                  <div className="req-card">
                    <span className="req-num">03</span>
                    <b>Working Microphone</b>
                    <small>Required for a friendly 2-minute voice verification with staff.</small>
                  </div>
                </div>

                <div className="gate-actions">
                  <a className="primary-btn discord-action-btn" href={loginUrl}>
                    <DiscordIcon size={20} /> Connect Discord &amp; Continue Application <ArrowIcon />
                  </a>
                  <a className="outline-link" href={inviteUrl} target="_blank" rel="noreferrer">
                    Join Discord Community First <ArrowIcon />
                  </a>
                </div>
              </div>
            ) : !user.inGuild ? (
              /* CASE 2: Logged in with Discord but NOT in guild */
              <div className="status-card warning apply-warning">
                <div className="warning-top">
                  <span className="warning-badge">⚠️ GUILD MEMBERSHIP REQUIRED</span>
                  <div className="user-tag-pill">
                    {user.avatarUrl && (
                      <img
                        src={user.avatarUrl}
                        alt={user.username}
                        className="user-avatar-tiny"
                      />
                    )}
                    <span>@{user.username}</span>
                  </div>
                </div>
                <h3>You are signed in, but haven&apos;t joined our server!</h3>
                <p>
                  Citizenship applications are processed directly through our Discord server, and your voice interview takes place in our HR voice channel. Join the official community server first, then refresh this page.
                </p>
                <div className="gate-actions" style={{ marginTop: '24px' }}>
                  <a className="discord-btn" href={inviteUrl} target="_blank" rel="noreferrer">
                    <DiscordIcon size={18} /> Join Official Discord Server
                  </a>
                  <button className="text-btn" onClick={() => window.location.reload()}>
                    I have joined, refresh status ↻
                  </button>
                </div>
              </div>
            ) : user.status === 'passed' ? (
              /* CASE 3: Already whitelisted */
              <div className="citizen-status-banner passed">
                <div className="status-header">
                  <span className="status-badge passed">✓ OFFICIALLY RATIFIED CITIZEN · WHITELIST ACTIVE</span>
                  <h2>Welcome Back, Citizen {user.mcUsername || user.displayName}!</h2>
                  <p>
                    Your citizenship is active for <strong>Season 3: The Sovereign Civilization</strong>. You have unrestricted access to all settlements, markets, and servers. You do not need to reapply.
                  </p>
                </div>

                <div className="status-server-details">
                  <div className="ip-box">
                    <div className="ip-box-top">
                      <small>JAVA EDITION SERVER IP</small>
                      <span className="port-tag">PORT: DEFAULT (25565)</span>
                    </div>
                    <b>play.ugeappagesmp.online</b>
                    <button onClick={() => copy('play.ugeappagesmp.online', 'java')} className="copy-action-btn">
                      <CopyIcon /> {copied === 'java' ? 'Copied IP!' : 'Copy Server IP'}
                    </button>
                  </div>
                  <div className="ip-box">
                    <div className="ip-box-top">
                      <small>BEDROCK EDITION PORT</small>
                      <span className="port-tag">GEAR / MOBILE / CONSOLE</span>
                    </div>
                    <b>19132</b>
                    <button onClick={() => copy('19132', 'bedrock')} className="copy-action-btn">
                      <CopyIcon /> {copied === 'bedrock' ? 'Copied Port!' : 'Copy Bedrock Port'}
                    </button>
                  </div>
                </div>

                <div className="status-actions">
                  <a className="primary-btn enchant" href="https://ugeappagesmp.online/profile">
                    Open Citizen Dossier &amp; Ledger <ArrowIcon />
                  </a>
                  <a className="outline-link" href="https://rules.ugeappagesmp.online">
                    Server Constitution <ArrowIcon />
                  </a>
                  <a className="ghost-link" href="https://ugeappagesmp.online/economy">
                    Economy &amp; Trade Guide <ArrowIcon />
                  </a>
                </div>
              </div>
            ) : user.status === 'pending' ? (
              /* CASE 4: Application pending review */
              <div className="citizen-status-banner pending">
                <div className="status-header">
                  <span className="status-badge pending">⏳ APPLICATION IN QUEUE · VOICE VERIFICATION NEEDED</span>
                  <h2>Application Received for {user.mcUsername || 'Citizen'}!</h2>
                  <p>
                    Your written submission has been logged into the staff review queue. Complete your quick voice interview to activate your in-game whitelist.
                  </p>
                </div>

                <div className="pending-instructions">
                  <h3>Next Steps to Complete Whitelist:</h3>
                  <ol>
                    <li>
                      <strong>Join Discord:</strong> Make sure you are in our official community server.
                    </li>
                    <li>
                      <strong>Enter Voice Lounge:</strong> Hop into the <strong>#Waiting for Interview</strong> voice channel.
                    </li>
                    <li>
                      <strong>2-Minute Verification:</strong> A staff member or admin will conduct a friendly verbal check, answer questions, and grant your whitelist on the spot.
                    </li>
                  </ol>
                  {user.updatedAt && (
                    <small className="pending-timestamp">
                      Application submitted: {new Date(user.updatedAt).toLocaleString()}
                    </small>
                  )}
                </div>

                <div className="status-actions">
                  <a className="primary-btn discord-action-btn" href={inviteUrl} target="_blank" rel="noreferrer">
                    <DiscordIcon size={18} /> Join #Waiting for Interview Channel <ArrowIcon />
                  </a>
                  <a className="outline-link" href="https://ugeappagesmp.online/profile">
                    View Profile &amp; Submission <ArrowIcon />
                  </a>
                </div>
              </div>
            ) : (
              /* CASE 5: Logged in, in guild, ready to fill form */
              <div className="apply-form-container">
                <WhitelistFlow user={user} onSubmitted={onUserChange} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Process Walkthrough Section */}
      <section className="section-pad apply-steps-section">
        <SectionLabel index="THE PROTOCOL">HOW CITIZENSHIP WORKS</SectionLabel>
        <div className="split-heading" data-reveal>
          <h2>Three simple steps.<br /><em>A lifetime in the realm.</em></h2>
          <p>
            We curate our community through a quick verification protocol so builders, merchants, and adventurers can play without griefers or cheats.
          </p>
        </div>

        <div className="apply-steps-grid" data-reveal>
          <div className="step-card">
            <span className="step-badge">STEP 01</span>
            <h3>Discord Identification</h3>
            <p>
              Connect your authentic Discord account. This binds your citizenship to a verified identity, ensuring server security and fair play.
            </p>
          </div>
          <div className="step-card">
            <span className="step-badge">STEP 02</span>
            <h3>Written Application</h3>
            <p>
              Provide your in-game username, birthdate, and tell us how you like to play. We verify age (13+) and respect for server rules.
            </p>
          </div>
          <div className="step-card">
            <span className="step-badge">STEP 03</span>
            <h3>2-Minute Voice Chat</h3>
            <p>
              Hop into our Discord voice waiting lounge for a brief greeting with our staff team to confirm rules and activate your whitelist live.
            </p>
          </div>
        </div>
      </section>

      {/* Constitution Pledge */}
      <section className="section-pad rules-pledge">
        <div data-reveal>
          <img src="/assets/ugeappage-logo-zoomed.webp" alt="Uge Appage SMP" />
          <small>THE CONSTITUTION OF CITIZENS</small>
          <h2>Read the laws of the realm.</h2>
          <p>
            Every citizen agrees to uphold our economic fairness, theft laws, and respectful conduct before taking their oath.
          </p>
          <div className="pledge-actions">
            <a className="outline-link" href="https://rules.ugeappagesmp.online">
              Read Full Rules &amp; Constitution <ArrowIcon />
            </a>
            <a className="ghost-link" href="https://ugeappagesmp.online/journey">
              Our Journey &amp; History <ArrowIcon />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
